import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setLanguage } from '../../../features/langSlice';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import PractitionerBadge from '../../Customer/components/PractitionerBadge';
import ReviewsMobile from '../../Customer/components/ReviewsMobile';
import MakeChoice from '../../Home/MakeChoice';
import DiscountBanner from '../../Home/DiscountBanner';
import useReviewStats from '../../../hooks/useReviewStats';
import { Review1, Review2, Review3, Review4, ReviewRating } from '../../../Constants';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import WechselHero from '../../../assets/wechsel-hero.webp';
import HeroImage from '../../../assets/hero-lp-pills.webp';

// Duplicated from src/Components/pages/Wechsel/index.jsx per the listicle brief
// (menopause-listicle-landing-page-brief.md). Same brand tokens, same shared
// components, warmer-traffic/retargeting format: "7 Signs Your Body Is Asking
// for Something Different" (headline direction B, recommended in the brief).
//
// Compliance note (same posture as /pages/wechsel): every point pairs one
// relatable moment with a general, non-diagnostic mechanism explainer — never
// "your perimenopausal fatigue," always the moment itself. Where a point has
// an EFSA-authorized nutrient claim, it's kept in its own visually separated
// "in your formula" block rather than blended into the mechanism sentence, so
// the two don't read together as one symptom-treatment claim. Point 1 (the
// supplement-shelf point) intentionally carries no ingredient claim — it's a
// logistics point, not an efficacy one. No unsourced botanicals (sage/
// saffron/boswellia), no outcome percentages, no physician-endorsement
// quotes, no competitor ingredient table — same guardrails as the reframe page.
const K = {
  HookLabel:            'LP_WechselListicle_HookLabel',
  H1:                   'LP_WechselListicle_H1',
  Setup:                'LP_WechselListicle_Setup',
  HeroCta:              'LP_WechselListicle_HeroCta',
  HeroImageAlt:         'LP_Wechsel_HeroImageAlt',
  PouchImageAlt:        'LP_Wechsel_PouchImageAlt',
  HeroSocialProof:      'LP_Pills_HeroSocialProof',
  CtaSub:               'LP_CtaSub',
  Point1Title:          'LP_WechselListicle_Point1Title',
  Point1Body:           'LP_WechselListicle_Point1Body',
  Point1ClaimLabel:     'LP_WechselListicle_Point1ClaimLabel',
  Point1Claim:          'LP_WechselListicle_Point1Claim',
  Review1Quote:         'BoomTestimonial9Review',
  Review1Name:          'BoomTestimonial9Name',
  CtaBanner1Heading:    'LP_WechselListicle_CtaBanner1Heading',
  Point2Title:          'LP_WechselListicle_Point2Title',
  Point2Body:           'LP_Wechsel_Focus2Phys',
  Point2Claim:          'LP_Wechsel_Focus2Desc',
  Review2Quote:         'BoomTestimonial2Review',
  Review2Name:          'BoomTestimonial2Name',
  Point3Title:          'LP_WechselListicle_Point3Title',
  Point3Body:           'LP_Wechsel_Focus4Phys',
  Point3Claim:          'LP_Wechsel_Focus4Desc',
  Point4Title:          'LP_WechselListicle_Point4Title',
  Point4Body:           'LP_Wechsel_Focus7Phys',
  Point4Claim:          'LP_Wechsel_Focus7Desc',
  Review4Quote:         'BoomTestimonial5Review',
  Review4Name:          'BoomTestimonial5Name',
  CtaBanner2Heading:    'LP_WechselListicle_CtaBanner2Heading',
  Point5Title:          'LP_WechselListicle_Point5Title',
  Point5Body:           'LP_Wechsel_Focus6Phys',
  Point5Claim:          'LP_Wechsel_Focus6Desc',
  Point6Title:          'LP_WechselListicle_Point6Title',
  Point6Body:           'LP_Wechsel_Focus3Phys',
  Point6Claim:          'LP_Wechsel_Focus3Desc',
  ClaimLabel:           'LP_WechselListicle_ClaimLabel',
  Point7Title:          'LP_WechselListicle_Point7Title',
  Point7Body:           'LP_WechselListicle_Point7Body',
  SolutionLabel:        'LP_WechselListicle_SolutionLabel',
  SolutionH2Line1:      'LP_WechselListicle_SolutionH2Line1',
  SolutionH2Line2:      'LP_WechselListicle_SolutionH2Line2',
  SolutionP1:           'LP_WechselListicle_SolutionP1',
  SolutionP2:           'LP_WechselListicle_SolutionP2',
  FocusSectionLabel:    'LP_Wechsel_FocusSectionLabel',
  FocusH2Line1:         'LP_Wechsel_FocusH2Line1',
  FocusH2Line2:         'LP_Wechsel_FocusH2Line2',
  FocusClaimLabel:      'LP_Wechsel_FocusClaimLabel',
  FocusMore:            'LP_Wechsel_FocusMore',
  FocusTab2:            'LP_Wechsel_FocusTab2',
  Focus2Title:          'LP_Wechsel_Focus2Title',
  Focus2Phys:           'LP_Wechsel_Focus2Phys',
  Focus2Desc:           'LP_Wechsel_Focus2Desc',
  FocusTab3:            'LP_Wechsel_FocusTab3',
  Focus3Title:          'LP_Wechsel_Focus3Title',
  Focus3Phys:           'LP_Wechsel_Focus3Phys',
  Focus3Desc:           'LP_Wechsel_Focus3Desc',
  FocusTab4:            'LP_Wechsel_FocusTab4',
  Focus4Title:          'LP_Wechsel_Focus4Title',
  Focus4Phys:           'LP_Wechsel_Focus4Phys',
  Focus4Desc:           'LP_Wechsel_Focus4Desc',
  FocusTab5:            'LP_Wechsel_FocusTab5',
  Focus5Title:          'LP_Wechsel_Focus5Title',
  Focus5Phys:           'LP_Wechsel_Focus5Phys',
  Focus5Desc:           'LP_Wechsel_Focus5Desc',
  FocusTab6:            'LP_Wechsel_FocusTab6',
  Focus6Title:          'LP_Wechsel_Focus6Title',
  Focus6Phys:           'LP_Wechsel_Focus6Phys',
  Focus6Desc:           'LP_Wechsel_Focus6Desc',
  FocusTab7:            'LP_Wechsel_FocusTab7',
  Focus7Title:          'LP_Wechsel_Focus7Title',
  Focus7Phys:           'LP_Wechsel_Focus7Phys',
  Focus7Desc:           'LP_Wechsel_Focus7Desc',
  ExpertHeading:        'LP_Wechsel_ExpertHeading',
  FinalHeading:         'LP_WechselListicle_FinalHeading',
  FinalSubline:         'LP_Wechsel_FinalSubline',
  FinalCta:             'LP_Wechsel_FinalCta',
  ReviewsHeading:       'LP_Wechsel_ReviewsHeading',
  AdDisclosure:         'LP_Pills_AdDisclosure',
  BadgeLabelLine1:      'LP_BadgeLabelLine1',
  BadgeLabelLine2:      'LP_BadgeLabelLine2',
  BadgeText:            'LP_BadgeText',
};

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true }) => {
  const { metaEvent } = useMeta();
  return (
  <CustomLink
    to="/quiz"
    onClick={() => metaEvent('Lead')}
    className="lp-btn-cta"
    style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, background: '#F6E147', color: '#103642',
      fontWeight: 700, fontSize: 19, borderRadius: 999,
      padding: '18px 36px', textDecoration: 'none',
      width: full ? '100%' : 'auto' }}
  >
    {children}
    <span aria-hidden="true">→</span>
  </CustomLink>
  );
};

const Section = ({ bg = '#fff', py = 56, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const StarRow = ({ count = 5 }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

const NumberBadge = ({ n }) => (
  <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SERIF,
    fontWeight: 400, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

// A single, hand-picked real customer review (sourced from riise's own reviews.io
// company page, already vetted/localized in this codebase's BoomTestimonial* keys)
// selected specifically because it speaks to this point — not a fabricated quote.
const ReviewQuote = ({ quote, name, isDesktop }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingLeft: isDesktop ? 4 : 0 }}>
    <svg width="20" height="16" viewBox="0 0 20 16" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M0 16V9.6C0 4.3 3.2.9 8 0l1 2.4C6.1 3.4 4.4 5.5 4.2 8H8v8H0zm11 0V9.6c0-5.3 3.2-8.7 8-9.6l1 2.4c-2.9 1-4.6 3.1-4.8 5.6H19v8h-8z" fill="#F6E147" />
    </svg>
    <Stack gap={4}>
      <p style={{ ...SANS, fontSize: 14.5, color: '#103642', margin: 0, lineHeight: 1.55, fontStyle: 'italic' }}>
        {quote}
      </p>
      <p style={{ ...SANS, fontSize: 12.5, color: 'rgba(16,54,66,0.5)', margin: 0, fontWeight: 600 }}>
        — {name}, verified riise customer
      </p>
    </Stack>
  </div>
);

// One numbered "sign": moment + general mechanism explainer, with the EFSA-authorized
// claim (if any) kept in its own visually separated block per the compliance brief —
// a beat between the physiology fact and the nutrient-function claim, not one sentence.
// Between reasons, only one carefully selected genuine customer review is allowed to
// appear — no icons, no wedged-in comparison tables, video carousels, or trust blocks.
const ListiclePoint = ({ n, title, body, claimLabel, claim, review, isDesktop }) => (
  <div style={{ display: 'flex', gap: isDesktop ? 24 : 16 }}>
    <NumberBadge n={n} />
    <Stack gap={12} style={{ flex: 1 }}>
      <p style={{ ...SANS, fontSize: isDesktop ? 19 : 17, fontWeight: 700,
        color: '#103642', margin: 0, lineHeight: 1.4 }}>
        {title}
      </p>
      <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)', margin: 0, lineHeight: 1.7 }}>
        {body}
      </p>
      {claim && (
        <div style={{ background: 'rgba(17,109,110,0.06)', border: '1px solid rgba(17,109,110,0.14)',
          borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
            <circle cx="10" cy="10" r="9" stroke="#116D6E" strokeWidth="1.4" />
            <path d="M6 10.2l2.6 2.6L14 7.5" stroke="#116D6E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div>
            <p style={{ ...SANS, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#116D6E', margin: '0 0 4px 0' }}>
              {claimLabel}
            </p>
            <p style={{ ...SANS, fontSize: 13.5, color: '#103642', margin: 0, lineHeight: 1.55 }}>
              {claim}
            </p>
          </div>
        </div>
      )}
      {review && <ReviewQuote quote={review.quote} name={review.name} isDesktop={isDesktop} />}
    </Stack>
  </div>
);

// "Where we focus" — reused from src/Components/pages/Wechsel/index.jsx. IM8-style
// pill-tab selector, one deep-dive card below. Physiology copy and the EFSA-authorized
// nutrient claim are kept visibly separate (own labeled line) rather than blended
// into one sentence, per the compliance brief. Mirrors the actual quiz/formula dosing
// logic (symptom → ingredient → dosing rule), not an invented list.
const FocusTabs = ({ t, K, isDesktop }) => {
  const [active, setActive] = useState(0);
  const tabs = [
    { tab: K.FocusTab2, title: K.Focus2Title, phys: K.Focus2Phys, claim: K.Focus2Desc },
    { tab: K.FocusTab3, title: K.Focus3Title, phys: K.Focus3Phys, claim: K.Focus3Desc },
    { tab: K.FocusTab4, title: K.Focus4Title, phys: K.Focus4Phys, claim: K.Focus4Desc },
    { tab: K.FocusTab5, title: K.Focus5Title, phys: K.Focus5Phys, claim: K.Focus5Desc },
    { tab: K.FocusTab6, title: K.Focus6Title, phys: K.Focus6Phys, claim: K.Focus6Desc },
    { tab: K.FocusTab7, title: K.Focus7Title, phys: K.Focus7Phys, claim: K.Focus7Desc },
  ];
  const current = tabs[active];

  return (
    <Stack gap={isDesktop ? 32 : 24}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10 }}>
        {tabs.map(({ tab }, i) => (
          <button
            key={tab}
            onClick={() => setActive(i)}
            style={{ ...SANS, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', borderRadius: 999, padding: '10px 20px',
              cursor: 'pointer', transition: 'all 0.15s ease',
              border: active === i ? '1px solid #F6E147' : '1px solid rgba(255,255,255,0.2)',
              background: active === i ? '#F6E147' : 'transparent',
              color: active === i ? '#103642' : 'rgba(255,255,255,0.75)' }}
          >
            {t(tab)}
          </button>
        ))}
        <span style={{ ...SANS, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', borderRadius: 999, padding: '10px 20px',
          border: '1px dashed rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' }}>
          {t(K.FocusMore)}
        </span>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20, padding: isDesktop ? 40 : 24 }}>
        <Stack gap={20}>
          <h3 style={{ ...SERIF, fontSize: isDesktop ? 30 : 24, lineHeight: 1.2, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(current.title)}
          </h3>
          <p style={{ ...SANS, fontSize: 15, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7 }}>
            {t(current.phys)}
          </p>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 20,
            display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
              <circle cx="10" cy="10" r="9" stroke="#F6E147" strokeWidth="1.4" />
              <path d="M6 10.2l2.6 2.6L14 7.5" stroke="#F6E147" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: '#F6E147', margin: '0 0 6px 0' }}>
                {t(K.FocusClaimLabel)}
              </p>
              <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.6 }}>
                {t(current.claim)}
              </p>
            </div>
          </div>
        </Stack>
      </div>
    </Stack>
  );
};

const CtaBanner = ({ heading, cta, isDesktop }) => (
  <Section bg="#103642" maxW={isDesktop ? 1200 : 480} px={isDesktop ? 80 : 24} py={40}>
    <Stack gap={16} align="center" style={{ textAlign: 'center' }}>
      <p style={{ ...SERIF, fontSize: isDesktop ? 26 : 21, lineHeight: 1.25, fontWeight: 400,
        color: '#fff', margin: 0, maxWidth: 480 }}>
        {heading}
      </p>
      <div style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
        <CtaButton full={!isDesktop}>{cta}</CtaButton>
      </div>
    </Stack>
  </Section>
);

const LangSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(localStorage.getItem('lang') || 'de');
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchTo = (lng) => {
    localStorage.setItem('lang', lng);
    i18n.changeLanguage(lng);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lang', lng);
    navigate({ search: newParams.toString() });
    setSelected(lng);
    dispatch(setLanguage({ lng }));
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        className="lp-lang-btn"
        style={{ ...SANS, background: 'none', border: '1px solid rgba(16,54,66,0.2)',
          borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#103642',
          display: 'flex', alignItems: 'center', gap: 5 }}
      >
        {selected.toUpperCase()}
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="#103642" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', borderRadius: 10, border: '1px solid #f0f0f0',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          overflow: 'hidden', minWidth: 110, zIndex: 200,
        }}>
          {[
            { code: 'de', label: 'Deutsch' },
            { code: 'en', label: 'English' },
          ].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => switchTo(code)}
              style={{ ...SANS, display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: selected === code ? '#f5f7f7' : '#fff',
                cursor: 'pointer', fontSize: 13, color: '#103642',
                fontWeight: selected === code ? 600 : 400, textAlign: 'left' }}
            >
              <i className={`flag-icon flag-icon-${code === 'en' ? 'us' : 'de'}`} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StickyBar = ({ t, K, isDesktop }) => {
  const { metaEvent } = useMeta();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
      transform: visible ? 'translateY(0)' : 'translateY(100%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.8)',
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          One sachet.<br />
          <span style={{ color: '#F6E147', fontWeight: 600 }}>Everything included.</span>
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

const WechselListicle = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reviewStats = useReviewStats();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // German is the default for this page regardless of the sitewide i18n
  // fallback (English) unless the URL explicitly says ?lang=en.
  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxW = isDesktop ? 640 : 480;
  const wideMaxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>7 Signs Your Body Is Asking for Something Different | riise.</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <DiscountBanner />

      {/* ── Header ── */}
      <header style={{ background: '#fff', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isDesktop ? '18px 80px' : '16px 20px' }}>
        <div style={{ width: 60, flexShrink: 0 }} />
        <img src={logo_home} alt="riise" style={{ height: 24, width: 'auto' }} />
        <LangSwitcher />
      </header>

      {/* ── Hook ── */}
      <Section bg="#F9F9F9" maxW={maxW} px={px} py={isDesktop ? 56 : 40}>
        <Stack gap={20} align="center" style={{ textAlign: 'center' }}>
          <PractitionerBadge />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: '#116D6E', background: 'rgba(17,109,110,0.1)',
              borderRadius: 999, padding: '6px 16px' }}>
              {t(K.HookLabel)}
            </span>
          </div>
          <h1 style={{ ...SERIF, fontSize: isDesktop ? 44 : 32, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.H1)}
          </h1>
          <p style={{ ...SANS, fontSize: isDesktop ? 17 : 15, color: 'rgba(16,54,66,0.65)',
            lineHeight: 1.7, margin: 0, maxWidth: 520 }}>
            {t(K.Setup)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StarRow />
            <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.6)', margin: 0 }}>
              {t(K.HeroSocialProof, {
                rating: reviewStats?.average || '4.88',
                count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
              })}
            </p>
          </div>
          <Stack gap={10} style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
            <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)', margin: 0, textAlign: 'center' }}>
              {t(K.CtaSub)}
            </p>
          </Stack>
        </Stack>
        <div style={{ marginTop: 40 }}>
          <img src={WechselHero} alt={t(K.HeroImageAlt)}
            style={{ width: '100%', maxWidth: isDesktop ? 560 : '100%', height: 'auto',
              borderRadius: 20, display: 'block', margin: '0 auto' }} loading="eager" />
        </div>
      </Section>

      {/* ── Review ticker ── */}
      <div style={{ background: '#103642', padding: '8px 0', overflow: 'hidden' }}>
        <style>{`@keyframes riiseMarqueeListicle { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div style={{ display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
          animation: 'riiseMarqueeListicle 45s linear infinite' }}>
          {[0, 1].map((dup) => (
            <div key={dup} style={{ display: 'flex', alignItems: 'center',
              color: '#F9F9F9', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14 }}>
              {[
                t(Review1), t(Review2), t(Review3), t(Review4),
                <span key="rating" style={{ color: '#F6E147', fontWeight: 600 }}>
                  {t(ReviewRating, { rating: reviewStats?.average || '4.88' })}
                </span>,
              ].map((item, i) => (
                <React.Fragment key={i}>
                  <span style={{ padding: '0 16px' }}>{item}</span>
                  <span style={{ color: '#F6E147' }}>·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Points 1-3 — the only thing allowed between reasons is a real review or a small
           illustration, nothing heavier ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={56}>
        <Stack gap={40}>
          <ListiclePoint n={1} isDesktop={isDesktop}
            title={t(K.Point1Title)} body={t(K.Point1Body)}
            claimLabel={t(K.Point1ClaimLabel)} claim={t(K.Point1Claim)}
            review={{ quote: t(K.Review1Quote), name: t(K.Review1Name) }} />
          <ListiclePoint n={2} isDesktop={isDesktop}
            title={t(K.Point2Title)} body={t(K.Point2Body)}
            claimLabel={t(K.ClaimLabel)} claim={t(K.Point2Claim)}
            review={{ quote: t(K.Review2Quote), name: t(K.Review2Name) }} />
          <ListiclePoint n={3} isDesktop={isDesktop}
            title={t(K.Point3Title)} body={t(K.Point3Body)}
            claimLabel={t(K.ClaimLabel)} claim={t(K.Point3Claim)} />
        </Stack>
      </Section>

      {/* ── CTA #1 ── */}
      <CtaBanner isDesktop={isDesktop} heading={t(K.CtaBanner1Heading)} cta={t(K.HeroCta)} />

      {/* ── Points 4-6 ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={56}>
        <Stack gap={40}>
          <ListiclePoint n={4} isDesktop={isDesktop}
            title={t(K.Point4Title)} body={t(K.Point4Body)}
            claimLabel={t(K.ClaimLabel)} claim={t(K.Point4Claim)}
            review={{ quote: t(K.Review4Quote), name: t(K.Review4Name) }} />
          <ListiclePoint n={5} isDesktop={isDesktop}
            title={t(K.Point5Title)} body={t(K.Point5Body)}
            claimLabel={t(K.ClaimLabel)} claim={t(K.Point5Claim)} />
          <ListiclePoint n={6} isDesktop={isDesktop}
            title={t(K.Point6Title)} body={t(K.Point6Body)}
            claimLabel={t(K.ClaimLabel)} claim={t(K.Point6Claim)} />
        </Stack>
      </Section>

      {/* ── CTA #2 ── */}
      <CtaBanner isDesktop={isDesktop} heading={t(K.CtaBanner2Heading)} cta={t(K.HeroCta)} />

      {/* ── Point 7 — the close, ties directly into the personalization/quiz mechanic ── */}
      <Section bg="#fff" maxW={maxW} px={px} py={56}>
        <ListiclePoint n={7} isDesktop={isDesktop}
          title={t(K.Point7Title)} body={t(K.Point7Body)} />
      </Section>

      {/* ── Our solution — what riise actually is, with the product visual ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={32}>
          <Stack gap={16} align="center" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#116D6E', background: 'rgba(17,109,110,0.1)',
                borderRadius: 999, padding: '6px 16px' }}>
                {t(K.SolutionLabel)}
              </span>
            </div>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 34 : 26, lineHeight: 1.15, fontWeight: 400,
              color: '#103642', margin: 0 }}>
              {t(K.SolutionH2Line1)}<br />
              <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.SolutionH2Line2)}</em>
            </h2>
            <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)', margin: 0, lineHeight: 1.7, maxWidth: 460 }}>
              {t(K.SolutionP1)}
            </p>
            <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)', margin: 0, lineHeight: 1.7, maxWidth: 460 }}>
              {t(K.SolutionP2)}
            </p>
          </Stack>
          <img src={HeroImage} alt={t(K.PouchImageAlt)}
            style={{ width: '100%', maxWidth: isDesktop ? 560 : '100%', height: 'auto',
              borderRadius: 20, display: 'block', margin: '0 auto' }} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Stack gap={10} style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
              <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)', margin: 0, textAlign: 'center' }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </div>
        </Stack>
      </Section>

      {/* ── Focus areas — click a symptom, see the result in your formula. Reused from
           /pages/wechsel so both pages stay consistent on this mechanic ── */}
      <Section bg="#103642" maxW={maxW} px={px} py={64}>
        <Stack gap={isDesktop ? 40 : 32}>
          <Stack gap={20}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#F6E147', background: 'rgba(246,225,71,0.12)',
                borderRadius: 999, padding: '6px 16px' }}>
                {t(K.FocusSectionLabel)}
              </span>
            </div>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 34 : 26, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0, textAlign: 'center' }}>
              {t(K.FocusH2Line1)}<br />
              <em style={{ fontStyle: 'italic', color: '#F6E147' }}>{t(K.FocusH2Line2)}</em>
            </h2>
          </Stack>
          <FocusTabs t={t} K={K} isDesktop={isDesktop} />
        </Stack>
      </Section>

      {/* ── Trust block — after the list, not wedged inside it ── */}
      <Section bg="#f5f7f7" maxW={wideMaxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 32 : 26, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
        </Stack>
      </Section>

      <Section bg="#fff" maxW={wideMaxW} px={px}>
        <MakeChoice />
      </Section>

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `72px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 38 : 27, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: 0, lineHeight: 1.7 }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 'auto' : '100%', maxWidth: 320 }}>
              <CtaButton full={!isDesktop}>{t(K.FinalCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)',
                textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      {/* ── Reviews carousel ── */}
      <Section bg="#f5f7f7" maxW={wideMaxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ReviewsHeading)}
          </h2>
          <ReviewsMobile />
        </Stack>
      </Section>

      <StickyBar t={t} K={K} isDesktop={isDesktop} />

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise"
              style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Privacy Policy
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default WechselListicle;
